import React, { useState, useCallback, useEffect } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Switch } from '../components/ui/switch';
import { Sheet, Search, CheckCircle2, AlertTriangle, Minus, Check, Loader2, ArrowRight, Clock, Bell, Copy, Info } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '../lib/formatters';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  new: 'bg-green-50 border-l-4 border-l-green-500',
  changed: 'bg-amber-50 border-l-4 border-l-amber-500',
  matched: 'bg-gray-50 border-l-4 border-l-gray-300 opacity-60',
};

const STATUS_BADGES = {
  new: <Badge className="bg-green-100 text-green-800" data-testid="badge-new">New Deal</Badge>,
  changed: <Badge className="bg-amber-100 text-amber-800" data-testid="badge-changed">Changed</Badge>,
  matched: <Badge className="bg-gray-100 text-gray-600" data-testid="badge-matched">Matched</Badge>,
};

const SheetImport = () => {
  const { getAuthHeaders } = useAuth();
  const [sheetId, setSheetId] = useState('');
  const [tabName, setTabName] = useState('');
  const [availableTabs, setAvailableTabs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [diffResult, setDiffResult] = useState(null);
  const [approvedRows, setApprovedRows] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);

  // Auto-sync state
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncEnabled, setSyncEnabled] = useState(false);
  const [pendingChanges, setPendingChanges] = useState([]);
  const [savingSync, setSavingSync] = useState(false);

  // Service-account setup info — shown upfront so users know they must share
  // their sheet with this specific email (NOT "anyone with the link").
  const [setupInfo, setSetupInfo] = useState(null);

  useEffect(() => {
    axios.get(`${API}/sheets/setup-info`, { headers: getAuthHeaders() })
      .then(res => setSetupInfo(res.data))
      .catch(() => { /* non-blocking */ });
  }, [getAuthHeaders]);

  const copySaEmail = useCallback(() => {
    if (!setupInfo?.service_account_email) return;
    navigator.clipboard.writeText(setupInfo.service_account_email)
      .then(() => toast.success('Service account email copied'))
      .catch(() => toast.error('Failed to copy'));
  }, [setupInfo]);

  const fetchSyncStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/sheets/sync/status`, { headers: getAuthHeaders() });
      setSyncStatus(res.data);
      setSyncEnabled(res.data.config?.enabled || false);
      if (res.data.config?.sheet_id && !sheetId) setSheetId(res.data.config.sheet_id);
      // Don't pre-fill tab — let the backend auto-select the first tab
    } catch { /* ignore */ }
    try {
      const res = await axios.get(`${API}/sheets/sync/pending`, { headers: getAuthHeaders() });
      setPendingChanges(res.data.pending || []);
    } catch { /* ignore */ }
  }, [getAuthHeaders, sheetId, tabName]);

  useEffect(() => { fetchSyncStatus(); }, [fetchSyncStatus]);

  const handleToggleSync = async (enabled) => {
    if (!sheetId.trim()) { toast.error('Enter a Sheet ID first'); return; }
    setSavingSync(true);
    try {
      if (enabled) {
        await axios.post(`${API}/sheets/sync/configure`, {
          sheet_id: sheetId.trim(), tab_name: tabName || null, enabled: true,
        }, { headers: getAuthHeaders() });
        toast.success('Auto-sync enabled — sheet will be checked every hour');
      } else {
        await axios.delete(`${API}/sheets/sync/configure`, { headers: getAuthHeaders() });
        toast.success('Auto-sync disabled');
      }
      setSyncEnabled(enabled);
      fetchSyncStatus();
    } catch (err) { toast.error('Failed to update sync settings'); }
    finally { setSavingSync(false); }
  };

  const handleDismissPending = async () => {
    try {
      await axios.post(`${API}/sheets/sync/dismiss`, {}, { headers: getAuthHeaders() });
      setPendingChanges([]);
      toast.success('Pending changes dismissed');
    } catch { toast.error('Failed to dismiss'); }
  };

  const handleFetchDiff = useCallback(async () => {
    if (!sheetId.trim()) { toast.error('Enter a Google Sheet ID'); return; }
    setLoading(true);
    setDiffResult(null);
    setApprovedRows(new Set());
    setSubmitResult(null);
    try {
      const res = await axios.post(`${API}/sheets/import`, {
        sheet_id: sheetId.trim(),
        tab_name: tabName || null,
      }, { headers: getAuthHeaders() });
      setDiffResult(res.data);
      setAvailableTabs(res.data.available_tabs || []);
      if (!tabName && res.data.tab_name) setTabName(res.data.tab_name);
      // If backend warned us (e.g. tab empty / no deal IDs / wrong tab), surface
      // it as a warning toast and DON'T claim success. The tab dropdown will
      // be populated so the user can switch.
      if (res.data.warning) {
        toast.warning(res.data.warning, { duration: 10000 });
      } else {
        toast.success(`Loaded ${res.data.summary.total} rows from "${res.data.tab_name}"`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to fetch sheet data');
    } finally { setLoading(false); }
  }, [sheetId, tabName, getAuthHeaders]);

  const toggleRow = (rowIndex) => {
    setApprovedRows(prev => {
      const next = new Set(prev);
      if (next.has(rowIndex)) next.delete(rowIndex);
      else next.add(rowIndex);
      return next;
    });
  };

  const approveAll = (statusType) => {
    if (!diffResult) return;
    const indices = diffResult.rows
      .filter(r => r.status_type === statusType)
      .map(r => r.row_index);
    setApprovedRows(prev => {
      const next = new Set(prev);
      indices.forEach(i => next.add(i));
      return next;
    });
  };

  const clearAll = () => setApprovedRows(new Set());

  const handleSubmit = async () => {
    if (approvedRows.size === 0) { toast.error('No rows approved'); return; }
    setSubmitting(true);
    setSubmitResult(null);
    try {
      const payload = diffResult.rows
        .filter(r => approvedRows.has(r.row_index))
        .map(r => ({
          row_index: r.row_index,
          action: r.status_type === 'new' ? 'create' : 'update',
          data: r.sheet_data,
          db_deal_id: r.db_deal_id,
          changes: r.changes,
        }));
      const res = await axios.post(`${API}/sheets/approve`, {
        sheet_id: sheetId.trim(),
        tab_name: tabName || null,
        approved_rows: payload,
      }, { headers: getAuthHeaders() });
      setSubmitResult(res.data);
      toast.success(`Created: ${res.data.created}, Updated: ${res.data.updated}`);
      setApprovedRows(new Set());
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply changes');
    } finally { setSubmitting(false); }
  };

  const approvedNewCount = diffResult?.rows?.filter(r => r.status_type === 'new' && approvedRows.has(r.row_index)).length || 0;

  return (
    <Layout>
      <div className="space-y-6 px-4 sm:px-0">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Sheet Import</h1>
          <p className="text-gray-500 mt-1">Import deals from a Google Sheet. Auto-detects headers from row 1.</p>
        </div>

        {/* Service-account share banner — surfaces the EXACT email the sheet
            must be shared with. 'Anyone with the link' does NOT grant access
            to service accounts; they have to be added by email. */}
        {setupInfo?.service_account_email && (
          <div
            className="rounded-md border border-blue-200 bg-blue-50 p-4 flex items-start gap-3"
            data-testid="sa-setup-banner"
          >
            <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-blue-900">
                Before importing — share your Google Sheet with this service account
              </p>
              <p className="text-sm text-blue-800 mt-1">
                <strong>"Anyone with the link" does NOT grant access.</strong> In your
                sheet, click <em>Share</em>, paste the address below as a Viewer, and Send.
              </p>
              <div className="mt-2 flex items-center gap-2">
                <code
                  className="text-xs sm:text-sm bg-white border border-blue-200 px-2 py-1 rounded font-mono text-blue-900 break-all"
                  data-testid="sa-email-text"
                >
                  {setupInfo.service_account_email}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copySaEmail}
                  data-testid="sa-email-copy-btn"
                  className="flex-shrink-0"
                >
                  <Copy className="h-3.5 w-3.5 mr-1" /> Copy
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Connection Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Sheet className="h-5 w-5 text-green-600" />
              Connect to Google Sheet
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-[280px]">
                <Label htmlFor="sheet-id">Sheet ID</Label>
                <Input
                  id="sheet-id"
                  data-testid="sheet-id-input"
                  placeholder="Paste Sheet ID or full URL"
                  value={sheetId}
                  onChange={e => {
                    // Paste-friendly: if a Sheets URL is pasted, extract the ID.
                    // Matches docs.google.com/spreadsheets/d/<ID>/edit, /view, etc.
                    const raw = e.target.value;
                    const m = raw.match(/\/d\/([a-zA-Z0-9-_]{20,})/);
                    setSheetId(m ? m[1] : raw);
                  }}
                />
                <p className="text-xs text-gray-400 mt-1">From the URL: docs.google.com/spreadsheets/d/<b>THIS_PART</b>/edit — or paste the full URL and we&apos;ll extract it.</p>
              </div>
              {availableTabs.length > 0 && (
                <div className="w-48">
                  <Label>Tab</Label>
                  <Select value={tabName} onValueChange={setTabName}>
                    <SelectTrigger data-testid="tab-select"><SelectValue placeholder="Select tab" /></SelectTrigger>
                    <SelectContent>
                      {availableTabs.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <Button onClick={handleFetchDiff} disabled={loading || !sheetId.trim()} data-testid="fetch-diff-btn">
                {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
                {loading ? 'Loading...' : 'Fetch & Compare'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Auto-Sync Config */}
        <Card className="border-blue-100">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-base">
                <Clock className="h-5 w-5 text-blue-600" />
                Hourly Auto-Sync
              </CardTitle>
              <div className="flex items-center gap-3">
                {syncStatus?.last_run && (
                  <span className="text-xs text-gray-400">
                    Last: {new Date(syncStatus.last_run.timestamp).toLocaleString()} — {syncStatus.last_run.status}
                  </span>
                )}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">{syncEnabled ? 'On' : 'Off'}</span>
                  <Switch
                    checked={syncEnabled}
                    onCheckedChange={handleToggleSync}
                    disabled={savingSync || !sheetId.trim()}
                    data-testid="sync-toggle"
                  />
                </div>
              </div>
            </div>
          </CardHeader>
          {syncEnabled && (
            <CardContent className="pt-0">
              <p className="text-xs text-gray-500">
                This sheet is checked every hour at :15. New/changed deals are queued below for your review.
              </p>
            </CardContent>
          )}
        </Card>

        {/* Pending Auto-Sync Changes */}
        {pendingChanges.length > 0 && (
          <Card className="border-amber-200 bg-amber-50/30">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-base text-amber-800">
                  <Bell className="h-5 w-5 text-amber-600" />
                  {pendingChanges.length} Pending Change{pendingChanges.length !== 1 ? 's' : ''} Detected
                </CardTitle>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={handleDismissPending} data-testid="dismiss-pending-btn">
                    Dismiss All
                  </Button>
                  <Button size="sm" onClick={() => {
                    // Load these into the diff review table
                    setDiffResult({
                      rows: pendingChanges.map(p => ({
                        row_index: p.id,
                        deal_id: p.deal_id,
                        deal_name: p.deal_name,
                        status_type: p.status_type,
                        changes: p.changes || [],
                        sheet_data: p.sheet_data || {},
                        db_deal_id: p.db_deal_id,
                      })),
                      summary: {
                        total: pendingChanges.length,
                        new: pendingChanges.filter(p => p.status_type === 'new').length,
                        changed: pendingChanges.filter(p => p.status_type === 'changed').length,
                        matched: 0,
                      },
                    });
                    toast.success('Loaded pending changes for review');
                  }} data-testid="review-pending-btn">
                    <ArrowRight className="h-4 w-4 mr-1" /> Review Changes
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="flex flex-wrap gap-2 text-sm">
                {pendingChanges.filter(p => p.status_type === 'new').length > 0 && (
                  <Badge className="bg-green-100 text-green-700">{pendingChanges.filter(p => p.status_type === 'new').length} new</Badge>
                )}
                {pendingChanges.filter(p => p.status_type === 'changed').length > 0 && (
                  <Badge className="bg-amber-100 text-amber-700">{pendingChanges.filter(p => p.status_type === 'changed').length} changed</Badge>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Summary */}
        {diffResult && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Card className="border-gray-200">
              <CardContent className="pt-4 text-center">
                <p className="text-2xl font-bold" data-testid="total-count">{diffResult.summary.total}</p>
                <p className="text-xs text-gray-500">Total Rows</p>
              </CardContent>
            </Card>
            <Card className="border-green-200 bg-green-50/50 cursor-pointer hover:bg-green-50" onClick={() => approveAll('new')}>
              <CardContent className="pt-4 text-center">
                <p className="text-2xl font-bold text-green-700" data-testid="new-count">{diffResult.summary.new}</p>
                <p className="text-xs text-green-600">New Deals (click to approve all)</p>
              </CardContent>
            </Card>
            <Card className="border-amber-200 bg-amber-50/50 cursor-pointer hover:bg-amber-50" onClick={() => approveAll('changed')}>
              <CardContent className="pt-4 text-center">
                <p className="text-2xl font-bold text-amber-700" data-testid="changed-count">{diffResult.summary.changed}</p>
                <p className="text-xs text-amber-600">Changed (click to approve all)</p>
              </CardContent>
            </Card>
            <Card className="border-gray-200">
              <CardContent className="pt-4 text-center">
                <p className="text-2xl font-bold text-gray-500" data-testid="matched-count">{diffResult.summary.matched}</p>
                <p className="text-xs text-gray-400">Already Matched</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Diff Table */}
        {diffResult && diffResult.rows.length > 0 && (
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="text-base">Review Changes</CardTitle>
                <div className="flex items-center gap-2">
                  {approvedRows.size > 0 && (
                    <Button size="sm" variant="ghost" onClick={clearAll}>Clear All</Button>
                  )}
                  <Badge variant="outline" data-testid="approved-count">
                    {approvedRows.size} approved
                  </Badge>
                  <Button
                    size="sm"
                    onClick={handleSubmit}
                    disabled={submitting || approvedRows.size === 0}
                    data-testid="apply-changes-btn"
                  >
                    {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ArrowRight className="h-4 w-4 mr-2" />}
                    Apply {approvedRows.size} Change{approvedRows.size !== 1 ? 's' : ''}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-100">
                      <TableHead className="w-12"></TableHead>
                      <TableHead className="w-16">Row</TableHead>
                      <TableHead>Deal ID</TableHead>
                      <TableHead>Business Name</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Funding</TableHead>
                      <TableHead className="text-right">Balance</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Changes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {diffResult.rows.map(row => (
                      <TableRow
                        key={row.row_index}
                        className={`${STATUS_COLORS[row.status_type]} ${approvedRows.has(row.row_index) ? 'ring-2 ring-blue-400 ring-inset' : ''}`}
                        data-testid={`diff-row-${row.row_index}`}
                      >
                        <TableCell>
                          {row.status_type !== 'matched' && (
                            <button
                              onClick={() => toggleRow(row.row_index)}
                              className={`h-6 w-6 rounded border-2 flex items-center justify-center transition-colors ${
                                approvedRows.has(row.row_index)
                                  ? 'bg-blue-600 border-blue-600 text-white'
                                  : 'border-gray-300 hover:border-blue-400'
                              }`}
                              data-testid={`approve-row-${row.row_index}`}
                            >
                              {approvedRows.has(row.row_index) && <Check className="h-4 w-4" />}
                            </button>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-gray-400">{row.row_index}</TableCell>
                        <TableCell className="font-mono font-medium">{row.deal_id}</TableCell>
                        <TableCell className="font-medium">{row.deal_name || '-'}</TableCell>
                        <TableCell className="text-sm">{row.sheet_data?.status || '-'}</TableCell>
                        <TableCell className="text-right text-sm">{row.sheet_data?.funding_amount ? formatCurrency(row.sheet_data.funding_amount) : '-'}</TableCell>
                        <TableCell className="text-right text-sm">{row.sheet_data?.balance ? formatCurrency(row.sheet_data.balance) : '-'}</TableCell>
                        <TableCell>{STATUS_BADGES[row.status_type]}</TableCell>
                        <TableCell>
                          {row.status_type === 'changed' && row.changes.length > 0 && (
                            <div className="space-y-1">
                              {row.changes.slice(0, 3).map(c => (
                                <div key={c.field} className="text-xs">
                                  <span className="font-medium text-gray-600">{c.field}:</span>{' '}
                                  <span className="text-red-500 line-through">{typeof c.db_value === 'number' ? c.db_value.toLocaleString() : String(c.db_value || '')}</span>{' '}
                                  <ArrowRight className="inline h-3 w-3 text-gray-400" />{' '}
                                  <span className="text-green-600 font-medium">{typeof c.sheet_value === 'number' ? c.sheet_value.toLocaleString() : String(c.sheet_value || '')}</span>
                                </div>
                              ))}
                              {row.changes.length > 3 && <p className="text-xs text-gray-400">+{row.changes.length - 3} more</p>}
                            </div>
                          )}
                          {row.status_type === 'new' && <span className="text-xs text-green-600">Will create new deal</span>}
                          {row.status_type === 'matched' && <Minus className="h-4 w-4 text-gray-300" />}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Submit Result */}
        {submitResult && (
          <Card className="border-green-200 bg-green-50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-6 w-6 text-green-600" />
                <div>
                  <p className="font-medium text-green-800">Import Complete</p>
                  <p className="text-sm text-green-600">
                    Created: {submitResult.created} | Updated: {submitResult.updated}
                    {submitResult.errors?.length > 0 && ` | Errors: ${submitResult.errors.length}`}
                  </p>
                </div>
              </div>
              {submitResult.errors?.length > 0 && (
                <div className="mt-3 space-y-1">
                  {submitResult.errors.map((e, i) => (
                    <div key={`err-${e.row}-${i}`} className="flex items-center gap-2 text-sm text-red-600">
                      <AlertTriangle className="h-4 w-4" /> Row {e.row}: {e.error}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Empty state */}
        {diffResult && diffResult.rows.length === 0 && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="py-8 text-center space-y-3" data-testid="empty-state">
              <AlertTriangle className="h-8 w-8 text-amber-500 mx-auto" />
              <p className="text-sm text-amber-900 max-w-2xl mx-auto leading-relaxed">
                {diffResult.warning
                  ? diffResult.warning
                  : 'No rows with a Deal ID found in the sheet. Make sure the first column contains deal IDs.'}
              </p>
              {availableTabs.length > 1 && (
                <p className="text-xs text-amber-700">
                  Switch tabs above and click <strong>Fetch &amp; Compare</strong> again.
                </p>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default SheetImport;
